import { useMemo, useRef, useState } from 'react';

import {
  Button,
  Divider,
  ErrorFillIcon,
  Input,
  Modal,
  Select,
  Text,
  type TypeSelectOption,
  dialogService,
  toastService,
} from '@thefarmersfront/kitchen';

import clsx from 'clsx';

import { ModalContentsContainer, ModalSection, SearchFilter, Table, TableTopField } from '~/components/common';

import { useGetOutboundsMutation, usePostShippingLabelsMutation } from '~/hooks/delivery-agency/queries';
import { addIf } from '~/utils';
import {
  getDeliveryAgencyCreatedShippingLabelsColumnDefs,
  getDeliveryAgencyOutboundUnitColumnDefs,
} from '~/utils/delivery-agency/table';

import { DELIVERY_AGENCY, TOAST_MESSAGE } from '~/constants';

import DeliveryAgencyTrackingCreateShippingLabelQuantityModal from '../DeliveryAgencyTrackingCreateShippingLabelQuantityModal';

import type { TypeInputStatus } from '~/types/common.types';
import type {
  TypeDeliveryAgencyCreatedShippingLabel,
  TypeDeliveryAgencyOutboundUnit,
  TypeDeliveryAgencyOutboundUnitSearchFilterValues,
} from '~/types/delivery-agency.types';

import styles from './DeliveryAgencyTrackingCreateShippingLabelModal.module.scss';

const CREATED_SHIPPING_LABELS_TABLE_HEIGHT = 244;
/** 검색 전·결과 없음 안내가 들어가는 높이: 헤더(44px) + 안내(120px) */
const OUTBOUND_UNIT_EMPTY_TABLE_HEIGHT = 164;

const KEYWORD_TYPE_OPTIONS: TypeSelectOption<string>[] = [
  {
    text: '출고번호',
    value: 'outboundCode',
  },
  {
    text: '운송장 번호',
    value: 'shippingLabelNumber',
  },
];

const INITIAL_SEARCH_FILTER_VALUES: TypeDeliveryAgencyOutboundUnitSearchFilterValues = {
  keywordType: KEYWORD_TYPE_OPTIONS[0],
  keyword: '',
};

const DeliveryAgencyTrackingCreateShippingLabelModal = ({
  open,
  onPrintShippingLabels,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  onPrintShippingLabels: (shippingLabelIds: number[], successCallback?: () => void | Promise<void>) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  const outboundsMutation = useGetOutboundsMutation();
  const shippingLabelsMutation = usePostShippingLabelsMutation();

  const [searchFilterValues, setSearchFilterValues] = useState<TypeDeliveryAgencyOutboundUnitSearchFilterValues>({
    ...INITIAL_SEARCH_FILTER_VALUES,
  });
  const [searchFilterValuesStatus, setSearchFilterValuesStatus] = useState<{
    keyword: TypeInputStatus;
  }>({ keyword: 'default' });
  const [outboundUnit, setOutboundUnit] = useState<TypeDeliveryAgencyOutboundUnit | null>(null);
  const [isOutboundUnitSearched, setIsOutboundUnitSearched] = useState<boolean>(false);
  // Modal은 z-index 8000으로 body에 portal되므로, 셀 툴팁도 모달 내부로 portal해야 모달 위에 표시된다.
  const outboundTableContainerRef = useRef<HTMLDivElement>(null);

  const [createdShippingLabels, setCreatedShippingLabels] = useState<TypeDeliveryAgencyCreatedShippingLabel[]>([]);
  const [isQuantityModalOpen, setIsQuantityModalOpen] = useState(false);

  const { columnDefs: outboundUnitColumnDefs } = useMemo(
    () => getDeliveryAgencyOutboundUnitColumnDefs(outboundTableContainerRef),
    [outboundTableContainerRef]
  );
  const { columnDefs: createdShippingLabelsColumnDefs } = useMemo(
    () => getDeliveryAgencyCreatedShippingLabelsColumnDefs(),
    []
  );

  const handleSearch = () => {
    if (searchFilterValues.keyword.trim() === '') {
      setSearchFilterValuesStatus({ keyword: 'error' });
      return;
    }

    outboundsMutation.mutate(
      {
        ...addIf(
          searchFilterValues.keywordType.value === 'outboundCode' && searchFilterValues.keyword,
          'outboundCode',
          searchFilterValues.keyword
        ),
        ...addIf(
          searchFilterValues.keywordType.value === 'shippingLabelNumber' && searchFilterValues.keyword,
          'shippingLabelNumber',
          searchFilterValues.keyword
        ),
      },
      {
        onSuccess: (data) => {
          setOutboundUnit(data);
          setIsOutboundUnitSearched(true);
        },
      }
    );
  };

  const handleSearchFilterReset = () => {
    setSearchFilterValues({ ...INITIAL_SEARCH_FILTER_VALUES });
    setSearchFilterValuesStatus({ keyword: 'default' });
  };

  const handleOpenQuantityModal = () => {
    if (!outboundUnit?.isCreatableShippingLabel) return;
    setIsQuantityModalOpen(true);
  };

  const handleCreateShippingLabels = (quantity: number) => {
    if (outboundUnit === null) return;

    shippingLabelsMutation.mutate(
      { outboundCode: outboundUnit.outboundCode, quantity },
      {
        onSuccess: (data) => {
          setIsQuantityModalOpen(false);
          setOutboundUnit(null);
          setCreatedShippingLabels((prev) => [...data, ...prev]);
          handleSearchFilterReset();
          setIsOutboundUnitSearched(false);
        },
        onError: (error) => {
          if (error?.code === DELIVERY_AGENCY.BACKEND_ERROR_DIALOG_CODE) {
            dialogService.addDialog({ children: error?.message });
            return;
          }

          toastService.addToast({ type: 'error', message: error?.message || TOAST_MESSAGE.DEFAULT_ERROR });
        },
      }
    );
  };

  const handleConfirm = () => {
    if (createdShippingLabels.length === 0) return;

    dialogService.addDialog({
      type: 'confirm',
      children: `운송장 ${createdShippingLabels.length}개를 출력하시겠습니까?`,
      onConfirm: () => {
        const createdShippingLabelIds = createdShippingLabels.map((label) => label.shippingLabelId);
        onPrintShippingLabels(createdShippingLabelIds, onConfirm);
      },
    });
  };

  const handleCancel = () => {
    if (createdShippingLabels.length > 0) {
      dialogService.addDialog({
        type: 'confirm',
        children: '추가 운송장을 출력하지 않고\n페이지를 나가시겠습니까?',
        confirmText: '나가기',
        onConfirm: onCancel,
      });
      return;
    }

    onCancel();
  };

  return (
    <>
      <Modal
        title="추가 운송장 생성"
        size="xlarge"
        confirmText="출력"
        disabled={createdShippingLabels.length === 0}
        open={open}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      >
        <ModalContentsContainer gap={8}>
          <div className={styles['guide-banner']}>
            <ErrorFillIcon className={styles['guide-banner-icon']} size={16} color="#7E3AB0" />
            <Text className={styles['guide-banner-text']} size="large" color="kurly" element="span">
              {'이미 등록되어 있는 주문에 새로운 '}
              <Text size="large" color="kurly" element="span" bold>
                운송장 번호를 추가로 발급
              </Text>
              {
                '하는 기능입니다.\n운송장 재출력이 필요한 경우에는 팝업 종료 후 리스트 상단에 있는 운송장 출력 버튼을 이용해 주세요.'
              }
            </Text>
          </div>
          <SearchFilter onSubmit={handleSearch} onReset={handleSearchFilterReset}>
            <div className={clsx(styles['filter-container'], styles['filter-keyword'])}>
              <Text size="small" weight="semi-bold" element="p">
                키워드
              </Text>
              <div className={styles['input-container']}>
                <Select
                  size="small"
                  placeholder="선택"
                  options={KEYWORD_TYPE_OPTIONS}
                  value={searchFilterValues.keywordType}
                  onChange={(_: unknown, value: TypeSelectOption<string>) => {
                    if (value?.value === searchFilterValues.keywordType?.value) return;
                    setSearchFilterValues({ ...searchFilterValues, keywordType: value, keyword: '' });
                    setSearchFilterValuesStatus({ keyword: 'default' });
                  }}
                />
                <Input
                  type="text"
                  size="small"
                  placeholder="입력"
                  maxLength={50}
                  status={searchFilterValuesStatus.keyword}
                  value={searchFilterValues.keyword}
                  onChange={(e) => {
                    setSearchFilterValues({ ...searchFilterValues, keyword: e.target.value });
                    setSearchFilterValuesStatus({ keyword: 'default' });
                  }}
                  onBlur={(e) => setSearchFilterValues({ ...searchFilterValues, keyword: e.target.value.trim() })}
                  onFocus={() => setSearchFilterValuesStatus({ keyword: 'default' })}
                />
              </div>
            </div>
          </SearchFilter>
          <Divider color="gray100" />
          <div className={styles['shipping-label-table-container']} ref={outboundTableContainerRef}>
            <TableTopField
              rightButton={
                <Button
                  size="tiny"
                  color="secondary"
                  disabled={!outboundUnit?.isCreatableShippingLabel}
                  onClick={handleOpenQuantityModal}
                >
                  운송장 생성
                </Button>
              }
            />
            {/* 운송장 번호 수만큼 행이 늘어나, 결과가 있으면 행 높이에 맞춘다 */}
            <Table
              autoHeight={!!outboundUnit}
              height={outboundUnit ? undefined : OUTBOUND_UNIT_EMPTY_TABLE_HEIGHT}
              className={styles['shipping-label-table']}
              data={outboundUnit ? [outboundUnit] : []}
              columns={outboundUnitColumnDefs}
              rowStatusType={isOutboundUnitSearched ? 'EMPTY' : 'DELIVERY_AGENCY_ADDITIONAL_SHIPPING_LABEL'}
            />
          </div>
          <Divider className={styles['section-divider']} color="gray100" />
          <ModalSection title="추가 운송장">
            <Table
              data={createdShippingLabels}
              columns={createdShippingLabelsColumnDefs}
              height={CREATED_SHIPPING_LABELS_TABLE_HEIGHT}
              rowStatusType="DELIVERY_AGENCY_ADDITIONAL_SHIPPING_LABEL_PRINT"
            />
          </ModalSection>
        </ModalContentsContainer>
      </Modal>
      {isQuantityModalOpen && (
        <DeliveryAgencyTrackingCreateShippingLabelQuantityModal
          open={isQuantityModalOpen}
          isCreating={shippingLabelsMutation.isPending}
          onConfirm={handleCreateShippingLabels}
          onClose={() => setIsQuantityModalOpen(false)}
        />
      )}
    </>
  );
};

export default DeliveryAgencyTrackingCreateShippingLabelModal;
